import React, { useEffect, useRef, useState } from "react";
import { TabStrip, TabStripTab } from "@progress/kendo-react-layout";
import WaveSurfer from "wavesurfer.js";
import RegionsPlugin from "wavesurfer.js/src/plugin/regions";
import "./HiHiDevice.scss";
import iconForward from "../../assets/images/icon-forward.svg";
import iconPause from "../../assets/images/icon-pause.svg";
import iconPlay from "../../assets/images/icon-play.svg";
import iconReplay from "../../assets/images/icon-replay.svg";
import { RecordingSummary } from "../../types/recording/RecordingSummary";
import { RecordingSegmentSentiment, RecordingTranscription } from "../../types/recording";
import { CombinedTranscription } from "../../types/transcription/CombinedTranscription";
import { RecordingTrackSegment, TrackSegment } from "../../types/player-track";
import { processRawTranscript } from "../../utils/transcriptUtils";
import { combineTranscriptSentiment } from "../../utils/transcriptSentimentUtil";
import { formatSecondsToTime } from "../../utils/dateTimeUtils";
import { RecordingAction } from "../../types/recording/RecordingAction";
import { extractSegmentSentimentInsights, extractTranscriptInsights, processTrackSegments } from "../../utils/trackInsightUtils";
import { useSearchParams } from "react-router-dom";
import { RecordingLockedSegment } from "../../types/recording/RecordingLockedSegment";
import hihiDeviceService from "../../services/hihiDevice.service";
import { AxiosError } from "axios";
import LoadingOverlay from "../../components/LoadingOverlay";
import recordingsService from "../../services/recordings.service";
import { RecordingPeaks } from "../../types/recording/RecordingPeaks";
import { API_BASE_URL } from "../../constants";
import { Loader } from "@progress/kendo-react-indicators";

const HiHiDevice: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const temporaryToken = searchParams.get("token");
  const callDateTime = searchParams.get("calldt") || undefined;
  const callHalfId = searchParams.get("callid") || undefined;

  const waveform = useRef<WaveSurfer | null>(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState("00:00:00");
  const [totalTime, setTotalTime] = useState("00:00:00");
  const [selectedTab, setSelectedTab] = useState(0);
  const [recordingDuration, setRecordingDuration] = useState<number>(0);
  const [recordingSummary, setRecordingSummary] = useState<RecordingSummary|undefined>(undefined);
  const [recordingTranscription, setRecordingTranscription] = useState<RecordingTranscription | undefined>(undefined);
  const [transcript, setTranscript] = useState<CombinedTranscription[] | undefined>(undefined);
  const [lockedSegments, setLockedSegments] = useState<RecordingLockedSegment[]>([]);
  const [sentimentsTrackInsight, setSentimentsTrackInsight] = useState<RecordingTrackSegment[]>([]);
  const [sentimentTrackSegments, setSentimentTrackSegments] = useState<TrackSegment[] | undefined>();
  const [participantsTrackInsight, setParticipantsTrackInsight] = useState<RecordingTrackSegment[]>([]);
  const [participantsTrackSegments, setParticipantsTrackSegments] = useState<TrackSegment[] | undefined>();
  const [recordingActions, setRecordingActions] = useState<RecordingAction[] | undefined>(undefined);
  const [recordingSentiments, setRecordingSentiments] = useState<RecordingSegmentSentiment[] | undefined>();
  const [hihiDeviceToken,setHihiDeviceToken] = useState<string|undefined>(undefined);
  const [loader,setLoader]=useState<boolean>(false);
  const [fatalError,setFatalError]=useState<string>("");
  const [error,setError]=useState<string>("");

  useEffect(()=>{
    if(temporaryToken){
      getAuthenticationToken(temporaryToken);
    }else{
      localStorage.removeItem("HihiDeviceToken");
      setHihiDeviceToken(undefined);
      setFatalError("The token is either invalid or has expired. Please try again with a valid token.");
    }
  },[]);

  useEffect(()=>{
    if(hihiDeviceToken){
      getCallDetails();
    }
  },[hihiDeviceToken])

  const getAuthenticationToken=async(temporaryToken:string)=>{
    setLoader(true);
    setFatalError("");
    try{
      const token = await hihiDeviceService.getAuthenticationToken(temporaryToken);
      localStorage.setItem("HihiDeviceToken",token);
      setHihiDeviceToken(token);
    }catch(error){
      if (error instanceof AxiosError) {
        localStorage.removeItem("HihiDeviceToken");
        setHihiDeviceToken(undefined);
        setFatalError("The token is either invalid or has expired. Please try again with a valid token.");
      }
    }finally{
      setLoader(false);
    }
  }

  const getCallDetails = async() => {
    setLoader(true);
    try{
      const callDetailData = await hihiDeviceService.getCallDetails(callHalfId,callDateTime);
      getCallDetailValues(callDetailData.recordings[0].id);
      await getRecordingPeaks(callDetailData.recordings[0].id);
    }catch(error){
      if (error instanceof AxiosError) {
        if(error.status===401){
          localStorage.removeItem("HihiDeviceToken");
          setHihiDeviceToken(undefined);
          setFatalError("The token is either invalid or has expired. Please try again with a valid token.");
        }else{
          setError(error.message);
        }
      }
    }finally{
      setLoader(false);
    }
  }

  const getCallDetailValues = async (recordingId:number) => {
    try {
      const data = await recordingsService.fetchRecordingCallDetailValues(recordingId,false,true);
      setRecordingTranscription(data.transcription ?? []);
      setRecordingSummary(data.summary ?? "")
      setRecordingSentiments(data.segmentSentiments ?? []);
      setLockedSegments(data.lockedSegments ?? []);
    } catch (err) {
      if (err instanceof AxiosError) {
        setRecordingSummary(undefined)
        setRecordingTranscription(undefined);
        setTranscript(undefined);
      }
    }
  };

  const getRecordingPeaks=async(recordingId:number)=>{
    var isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    try{
      const recPeaks: RecordingPeaks | string = await recordingsService.fetchRecordingPeaks(recordingId ?? 0, false, true);

      if (typeof recPeaks !== "string") {
        if (!waveform.current && hihiDeviceToken) {
          waveform.current = WaveSurfer.create({
            barWidth: 3,
            barRadius: 3,
            barGap: 2,
            barMinHeight: 1,
            cursorWidth: 1,
            container: '#waveformContainer',
            waveColor: "#555",
            progressColor: "#fff", //"#00bcd4",
            cursorColor: "#fff",
            height: 100,
            responsive: true,
            plugins: [
              RegionsPlugin.create({
                dragSelection: false,
              }),
            ],
          });

          waveform.current.load(
            `${API_BASE_URL}/recording/${recordingId}/play?access_token=${recPeaks.aliasToken}&isSafari=${isSafari}`, recPeaks.peaks
          );

          waveform.current.on("ready", () => {
            setTotalTime(formatSecondsToTime(waveform.current?.getDuration() ?? 0));
            setRecordingDuration(waveform.current?.getDuration() ?? 0);
          });

          waveform.current.on("audioprocess", () => {
            setCurrentTime(formatSecondsToTime(waveform.current?.getCurrentTime() ?? 0));
          });

          waveform.current.on("play", () => setIsPlaying(true));
          waveform.current.on("pause", () => setIsPlaying(false));
        }
      }

    }catch(err){
      if(err instanceof AxiosError){
        throw err;
      }
    }
  }

  useEffect(() => {
    if (recordingTranscription) {
      const transcriptionInsight = extractTranscriptInsights(recordingTranscription, recordingDuration, (key: string, defaultValue: string) => { return defaultValue });
      setParticipantsTrackInsight(transcriptionInsight);
      const participantSegments = processTrackSegments(transcriptionInsight, recordingDuration);
      setParticipantsTrackSegments(participantSegments);
      const processedTranscript = processRawTranscript(recordingTranscription, lockedSegments.length);
      const combinedTranscript = combineTranscriptSentiment(processedTranscript, sentimentsTrackInsight);
      setTranscript(combinedTranscript);
    }
  }, [recordingTranscription, lockedSegments, recordingDuration, sentimentsTrackInsight]);

  useEffect(() => {
    const sentimentInsights = extractSegmentSentimentInsights(recordingSentiments ? recordingSentiments : []);
    setSentimentsTrackInsight(sentimentInsights);
    const sentimenttSegments = processTrackSegments(sentimentInsights, recordingDuration);
    setSentimentTrackSegments(sentimenttSegments);
  }, [recordingSentiments, recordingDuration]);

  useEffect(() => {
    if(waveform.current && lockedSegments && lockedSegments.length > 0 && hihiDeviceToken){
      waveform.current.clearRegions();
      lockedSegments.forEach(segment => {
        if(waveform.current && segment.isLocked){
          waveform.current.addRegion({
            id: segment.recordingSegment.segmentId,
            start: segment.recordingSegment.startTime,
            end: segment.recordingSegment.endTime,
            color: "rgba(255, 255, 255, 0.3)",
            drag: false,
            resize: false
          });
          const element = document.querySelector(`[data-id="${segment.recordingSegment.segmentId}"]`);
          if(element){
            const lockId = "addLockButton" + segment.recordingSegment.segmentId;
            const new_html = element?.innerHTML +
                  `<div class='regionBtn m-t-15'>
                      <div id=${lockId} class='recordingRegionBtn p-r-3' title='Locked'>
                        <i class="bi bi-lock-fill fs-17"></i>
                      </div>
                    </div>`;
            element.innerHTML = new_html;
          }
        }
      });
    }
  }, [lockedSegments, waveform.current,hihiDeviceToken]);

  // controls
  const handlePlayPause = () => {
    waveform.current?.playPause();
  };

  const handleRewind = () => {
    if (waveform.current) {
      waveform.current.setCurrentTime(Math.max(0, waveform.current.getCurrentTime() - 10));
    }
  };

  const handleForward = () => {
    if (waveform.current) {
      waveform.current.setCurrentTime(Math.min(waveform.current.getDuration(), waveform.current.getCurrentTime() + 10));
    }
  };

  return (
    <div className="hihi-device">
      {(loader && !hihiDeviceToken) ? (
        <LoadingOverlay
          customStyle={{ position: "fixed", marginTop: "55px" }}
          themeColor={"light"}
          size={"medium"}
          loadingText={"Setting up things for you. Please wait."}
        />
      ) : (
        <>
        {(!loader && !hihiDeviceToken && fatalError) ? (
          <div>
            <p className="fatalErrorText">{fatalError}</p>
          </div>
        ) : (
          <>
          {/* <div className="top-section"></div> */}
          <div className="sections">
            <section className="section section-left">
              <div className="player-section">
                <div className="player-regions">
                  <div id='waveformContainer' className="waveform">
                    {loader && (
                      <div className="d-flex align-items-center justify-content-center">
                        <Loader type={"infinite-spinner"} />
                      </div>
                    )}
                    {error && (
                      <div className="d-flex align-items-center p-t-30 p-b-40 justify-content-center">
                        <span className="text-muted">
                          <i className="bi bi-exclamation-triangle-fill tx-amber"></i>{" "}
                          {"Can't play recording right now! Please try again..."}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="call-track-details">
                  <h6>Participation:</h6>
                  <div className="track-single">
                    {participantsTrackSegments ? participantsTrackSegments.map((s) => {
                      var classCss = "";
                      if (s.cssLabel.startsWith('speaker')) {
                        let speakerId = parseInt(s.cssLabel.split('-')[1]);
                        classCss = `participant-${(speakerId % 2) + 1}`
                      }
                      return (
                        <span key={s.id} className={classCss} style={{ width: `${s.width}%`, left: `${s.left}%` }}></span>
                      )
                    }) : (
                      <span className="" style={{ width: `100%`, left: `0%` }}></span>
                    )}
                  </div>
                  <div className="call-track-index">
                    <div className="index-single">
                      <span className="participant-1"></span> Participant 1
                    </div>
                    <div className="index-single">
                      <span className="participant-2"></span> Participant 2
                    </div>
                  </div>
                </div>
                <div className="call-track-details">
                  <h6>Sentiments:</h6>
                  <div className="track-single">
                    {sentimentTrackSegments ? sentimentTrackSegments.map((s) => {
                      return (
                        <span key={s.id} className={s.label.toLowerCase()} style={{ width: `${s.width}%`, left: `${s.left}%` }}></span>
                      )
                    }) : (
                      <span className="" style={{ width: `100%`, left: `0%` }}></span>
                    )}
                  </div>
                  <div className="call-track-index">
                    <div className="index-single">
                      <span className="positive "></span> Positive
                    </div>
                    <div className="index-single">
                      <span className="neutral"></span> Neutral
                    </div>
                    <div className="index-single">
                      <span className="negative"></span> Negative
                    </div>
                  </div>
                </div>
              </div>

              <div className="player-controls">
                <div className="time">{currentTime}</div>
                <div className="player-controls-button">
                  <button onClick={handleRewind}><img src={iconReplay} alt="Rewind" /></button>
                  <button className="play-pause" onClick={handlePlayPause}>
                    {isPlaying ? (
                      <img src={iconPause} alt="Pause" />
                    ) : (
                      <img src={iconPlay} alt="Play" />
                    )}
                  </button>
                  <button onClick={handleForward}><img src={iconForward} alt='Forward' /></button>
                </div>
                <div className="time">{totalTime}</div>
              </div>
            </section>

            <section className="section section-right">
              <TabStrip selected={selectedTab} onSelect={(e) => setSelectedTab(e.selected)} animation={true} >
                <TabStripTab title="AI Summary">
                  <div className="tab-content">
                    {loader ? (
                      <div className="d-flex justify-content-center align-items-center"><Loader/></div>
                    ):(
                      <>
                      {(recordingSummary===undefined) ? (
                        <div className="no-text">
                          We cannot fetch summary at the moment. Please try again later.
                        </div>
                      ) : (
                        <>
                        {recordingSummary && recordingSummary.callSummary?.length !== 0 ? (
                          <div className="content-inner">
                            <p>{recordingSummary?.callSummary}</p>
                          </div>
                        ) : (
                          <div className="no-text">
                            No summary exist for this call
                          </div>
                        )}
                      </>
                    )}
                  </>
                  )}
                  </div>
                </TabStripTab>

                <TabStripTab title="Transcript">
                  <div className="tab-content">
                    {(loader) ? (
                      <div className="d-flex justify-content-center align-items-center"><Loader/></div>
                    ):(
                      <>
                      {(transcript===undefined) ? (
                        <div className="no-text">
                          We cannot fetch transcription at the moment. Please try again later.
                        </div>
                      ) : (
                        <>
                        {transcript && transcript.length > 0 ?
                          transcript.map((t) => {
                            var classCss = `participant-${(t.speaker % 2) + 1}`;
                            return (
                              <div key={t.id} className={`content-inner participant ${classCss}`}>
                                <div className="participant-top">
                                  <span className="participant-name">{`${"Participant"} ${t.speaker + 1}`}</span>
                                  <span className="participant-duration">{formatSecondsToTime(t.start)}</span>
                                </div>
                                {t.text}
                              </div>
                            );
                          })
                        : (
                          <div className="no-text">
                            No transcription exist for this call
                          </div>
                        )}
                      </>
                    )}
                  </>
                  )}
                  </div>
                </TabStripTab>

                {/* <TabStripTab title="Actions">



                  <div className="tab-content">
                    {(loader) ? (
                      <div className="d-flex justify-content-center"><Loader/></div>
                    ):(
                      <>
                      {(recordingActions===undefined) ? (
                        <div className="no-text">
                          We cannot fetch actions at the moment. Please try again later.
                        </div>
                      ) : (
                        <>
                        {recordingActions && recordingActions.length > 0 ?
                          recordingActions.map((action) => {
                            return (
                              <div key={action.id} className="content-inner">
                                {action.action}
                              </div>
                            )
                          })
                        : (
                          <div className="no-text">
                            No actions exist for this call
                          </div>
                        )}
                      </>
                    )}
                  </>
                  )}
                  </div>
                </TabStripTab> */}
              </TabStrip>
            </section>
          </div>
          </>
        )}
        </>
        )}
    </div>
  );
};

export default HiHiDevice;
